import { SidebarItem } from "@backstage/core-components";
import { APP_COMMIT } from "@internal/backstage-plugin-deploy-common";
import { CircularProgress, Tooltip, Typography } from "@material-ui/core";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import SystemUpdateAltOutlinedIcon from "@material-ui/icons/SystemUpdateAltOutlined";

import { usePlatformAdmin } from "../identity/ownership";

import { type DeployIndicator, useDeployIndicator } from "./deployStatus";

const REPO_URL = "https://imcg.ghe.com/thetechcollective/backstage-im-dev";

/**
 * The spinning wheel for a rollout in progress.
 *
 * `color="inherit"` so it takes the sidebar's own text colour — the MOTION is the signal, not a loud
 * accent. Same reasoning (and roughly the same size) as the in-flight wheel on the My AVD status bar.
 */
const RollingOutIcon = () => <CircularProgress size={18} thickness={5} color="inherit" />;

/**
 * How each state presents itself. The item ALWAYS shows the short commit — that is its job, and
 * swapping the text for a message would cost the provenance the item exists to give — so the icon
 * plus the tooltip carry the rollout state.
 */
function presentation(indicator: DeployIndicator, short: string) {
	switch (indicator) {
		case "rolling-out":
			return { icon: RollingOutIcon, tooltip: "An update is rolling out, stay tuned" };
		case "awaiting-approval":
			return {
				icon: HourglassEmptyOutlinedIcon,
				// Named for what it is. A wheel here would spin for as long as it takes somebody to
				// click approve — hours, sometimes — while appearing to mean "nearly there".
				tooltip: "An update is built and waiting for approval before it rolls out",
			};
		case "stale":
			return {
				icon: SystemUpdateAltOutlinedIcon,
				tooltip: "A newer version is live — click to reload and pick it up",
			};
		default:
			return {
				icon: CodeOutlinedIcon,
				tooltip: `Running commit ${short} — click to view it on GHE`,
			};
	}
}

/**
 * Sidebar indicator showing the git commit the running image was built from (build provenance), plus
 * whether a NEWER one is on its way.
 *
 * Reads APP_COMMIT — a compiled constant stamped into the bundle at build time (no runtime config) —
 * and, at runtime, `/api/deploy/status` for the pipeline's state.
 *
 * WHY THE WRAPPING `<div>`: `SidebarItem` forwards its ref but, on the button branch it takes when
 * given `onClick` rather than `to`, it spreads only its own `onClick`/`className` onto the button and
 * DROPS unknown props. MUI v4's Tooltip works by cloning its child with `onMouseOver`/`onFocus`
 * handlers, so wrapping `SidebarItem` directly would silently produce an item with no tooltip. The
 * div is the element those handlers land on.
 */
const CommitIndicator = () => {
	const indicator = useDeployIndicator();
	const short = APP_COMMIT.slice(0, 7);
	const { icon, tooltip } = presentation(indicator, short);

	// A stale tab's own commit is no longer the interesting one — the reload is. Elsewhere the click
	// keeps opening the commit on GHE.
	const onClick =
		indicator === "stale"
			? () => window.location.reload()
			: () =>
					window.open(
						`${REPO_URL}/commit/${APP_COMMIT}`,
						"_blank",
						"noopener,noreferrer",
					);

	return (
		<Tooltip title={tooltip}>
			<div>
				<SidebarItem icon={icon} text={short} onClick={onClick} />
				{/* NEVER HOVER-ONLY (same rule the disabled AVD cards follow): a MUI v4 tooltip only
				exists in the DOM while the pointer is on it, and the icon that carries the state
				visually says nothing to a screen reader. `srOnly` puts the same sentence in the
				accessibility tree permanently, at zero visual cost. Omitted at rest, where the
				button's own label — the commit — already says everything there is to say. */}
				{indicator !== "current" && <Typography variant="srOnly">{tooltip}</Typography>}
			</div>
		</Tooltip>
	);
};

/**
 * Renders nothing when the commit is empty (local dev), and nothing for anybody who is not a PLATFORM
 * ADMIN. Either way the poll in {@link useDeployIndicator} never starts, because the hook lives
 * one component down — so most of the company never calls `/api/deploy/status` every minute.
 *
 * WHY ADMIN-ONLY: the item's payload is a git SHA linking into a private GHE repo. A colleague who
 * clicks it gets a 404 from a repo they have no account on, so it offers them nothing but a dead end;
 * for the platform team it is the build provenance of the tab they are looking at.
 *
 * The cost, accepted deliberately: the `stale` state's "click to reload and pick it up" nudge goes
 * with it, so a non-admin's old tab stays old until they reload it themselves.
 */
export const DeployedCommit = () => {
	const { admin } = usePlatformAdmin();
	if (!APP_COMMIT || !admin) {
		return null;
	}
	return <CommitIndicator />;
};
